import React from 'react';
import styles from './captcha.module.scss';
import { delay, isEmptyString, isNullOrUndefined } from "../common/utility";

import { useApiServiceContextContext } from "../contexts/apiServiceContext";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { ProgressBar } from "primereact/progressbar";

/** https://www.npmjs.com/package/react-simple-captcha */
import { loadCaptchaEnginge, LoadCanvasTemplate, validateCaptcha } from 'react-simple-captcha';

/** Displays a CAPTCHA form
 * @param {*} props - ```{ captchaDigits = 6..12, onCaptureStatusChanged = (captchaSuccess = true|false) => void, title = "", subTitle = "" }```
 * @returns
 */
const AcquireCaptcha = (props) => {
	const apiServiceContext = useApiServiceContextContext();
	const refInputBox = React.useRef(null);
	const [isWorking, setWorkingState] = React.useState(false);
	const [once, setOnce] = React.useState(false);
	const [userInput, setUserInput] = React.useState("");
	const [userInputState, setUserInputState] = React.useState({
		isValid: false,
		message: "",
	});

	const [capctchaStatus, setCapctchaStatus] = React.useState({
		count: 0,
		isValid: false,
	});

	React.useEffect(() => {
		console.debug(`AcquireCaptcha.useEffect([]) -> ${once}`);
		if (once) {
			return;
		}

		const digits = apiServiceContext?.configuration?.captchaDigits || props?.captchaDigits || 6;
		const captchaDigits = Math.min(Math.max(digits, 6), 12);

		console.debug(`AcquireCaptcha.useEffect([]) -> setOnce...`);
		loadCaptchaEnginge(captchaDigits, "black", "white");
		setOnce(true);
	}, []);

	React.useEffect(() => {
		console.debug(`AcquireCaptcha.useEffect([capctchaStatus]) capctchaStatus=${capctchaStatus}, userInputState.count=${userInputState.count}`);
		if ((typeof capctchaStatus !== "undefined") && (capctchaStatus !== undefined)) {
			props?.onCaptureStatusChanged?.(capctchaStatus);
		}
	}, [capctchaStatus]);

	React.useEffect(() => {
		console.debug(`AcquireCaptcha.useEffect([isWorking]) isWorking=${isWorking}`);
		// if not working use the input box ref to set focus to the input control
		if (!isWorking) {
			console.debug(`AcquireCaptcha.useEffect([isWorking]) ➜ Setting focus to input box`);
			refInputBox?.current?.focus();
		}
	}, [isWorking]);

	React.useEffect(() => {
		// when the managed service changes, automatically focus the captcha input
		console.debug(`AcquireCaptcha.useEffect([apiServiceContext.serviceDetails]) ➜ Setting focus to input box`);
		refInputBox?.current?.focus();
	}, [apiServiceContext.serviceDetails]);

	const resetForm = () => {
		console.debug(`AcquireCaptcha:resetForm() ➜ Resetting form state`);
		setUserInput("");
		setUserInputState({
			isValid: false,
			message: "",
		});
		setCapctchaStatus({
			count: 0,
			isValid: false,
		});
		setWorkingState(false);
	};

	const onUserInputChanged = (e) => {
		const emState = `${userInput || ""}`.trim();
		const emInputText = `${e?.target?.value || ""}`.trim();
		console.debug(`AcquireCaptcha:onUserInputChanged() ➜ emInputText: ${emInputText}, emState: ${emState || ""}`);
		setUserInput(emInputText);
	};

	const validateUserInput = async (captchaInput) => {
		const emState = `${userInput || ""}`.trim();
		const emInputText = `${captchaInput || ""}`.trim();
		console.debug(`AcquireCaptcha:validateUserInput() ➜ captchaInput: ${emInputText}, emState: ${emState || ""}`);

		setWorkingState(true);
		try {
			/* validate the capctha input */
			const isValid = validateCaptcha(emInputText);
			console.debug(`AcquireCaptcha:validateUserInput() ➜ isValid: ${isValid}`);

			setUserInputState({
				isValid,
				message: isValid ? "CAPTCHA validation was successful, please wait..." : "Your input did not match the CAPTCHA value, please try again"
			});

			/** pause for effect, also makes it more tedious to continually blat wrong captcha values at the UI... */
			await delay(1000);

			setCapctchaStatus((prevState) => ({
				count: prevState.count + 1,
				isValid
			}));
			if (!isValid) {
				setUserInput("");
			}
		} catch (err) {
			console.error(`AcquireCaptcha:validateUserInput() ➜ Error:`, err);
			setCapctchaStatus((prevState) => ({
				count: prevState.count + 1,
				isValid: false
			}));
			setUserInputState({
				isValid: false,
				message: "An error occured trying to validate the captcha"
			});
		} finally {
			setWorkingState(false);
		}
	};

	const { title, subTitle } = props;
	const isApiServiceDisconnected = isNullOrUndefined(apiServiceContext?.serviceDetails);
	const { isApiConnecting } = apiServiceContext;
	return (
		<div className={styles.captchaContainer}>
			<div className={styles.headerSection}>
				<h1 className="title">{title || ""}</h1>
				<h2 className="subtitle"
					dangerouslySetInnerHTML={{ __html: subTitle || "" }}
				/>
			</div>

			<div className="field">
				<div className={styles.captchaCanvas}>
					<LoadCanvasTemplate
						reloadText="Regenerate Captcha"
					/>
				</div>
			</div>

			<div className="field">
				<label className="label">Enter CAPTCHA</label>
				<div className="columns">
					<div className="column">
						<div className="control">
							<InputText
								ref={refInputBox}
								tabIndex={"0"}
								disabled={isApiServiceDisconnected || isWorking}
								required
								name="captcha"
								placeholder="Please enter the captcha value"
								value={userInput}
								onChange={(e) => onUserInputChanged(e)}
								onKeyUp={(e) => {
									console.debug("userInput.onKeyUp", e);
									if ((e.code === "Enter") || (e.key === "Enter")) {
										validateUserInput(userInput);
									}
								}}
							/>
							{!isEmptyString(userInputState.message) && (
								<p className={`help ${userInputState.isValid ? "is-success" : "is-danger"}`}>
									{userInputState.message}
								</p>
							)}
						</div>
					</div>
					<div className="column is-2 field">
					</div>
				</div>
			</div>

			<div className="field">
				<div className="columns">
					<div className="column">
						<div className="control">
							<Button
								className={"p-button-wide"}
								disabled={isApiConnecting || isApiServiceDisconnected || isEmptyString(userInput)}
								onClick={() => {
									validateUserInput(userInput);
								}}
								loading={isWorking}
								label="Submit"
							/>
							<div className={styles.progressBar}>
								{isWorking && (
									<ProgressBar mode="indeterminate" style={{ height: '6px' }}></ProgressBar>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default AcquireCaptcha;
